// utils/timeUtils.js - 한국시간 관련 유틸리티 함수들

/**
 * 현재 한국시간을 ISO 문자열로 반환
 * @returns {string} 한국시간 ISO 문자열 (예: "2024-01-15T14:30:00.000+09:00")
 */
export const getKoreanTimeISO = () => {
    const now = new Date();
    // 한국시간으로 변환하여 ISO 문자열 반환
    return now.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' }).replace(' ', 'T') + '.000+09:00';
};

/**
 * 현재 한국시간을 Date 객체로 반환
 * @returns {Date} 한국시간 Date 객체
 */
export const getKoreanTime = () => {
    const now = new Date();
    // 한국시간대의 현재 시간을 Date 객체로 반환
    const koreanTimeString = now.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' });
    return new Date(koreanTimeString + '+09:00');
};

/**
 * 한국 시간대 기준 YYYY-MM-DD 키를 반환 (연속 출석 등 일자 비교용)
 * @param {Date} baseDate - 기준 Date (기본값: 지금)
 * @returns {string} YYYY-MM-DD (KST)
 */
// (streak 기능 제거로 사용하지 않음)

/**
 * 한국 시간대 기준 YYYY-MM-DD 키 반환 (일간 식별자)
 */
export const getKoreanDateKey = (baseDate = new Date()) => {
    const kst = baseDate.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' });
    return kst.split(' ')[0]; // YYYY-MM-DD
};

/**
 * 한국 시간대 기준 YYYY-Www 키 반환 (주간 식별자, ISO 주차 유사)
 * 주 시작: 월요일 기준
 */
export const getKoreanWeekKey = (baseDate = new Date()) => {
    // KST 기준 날짜로 변환
    const kstString = baseDate.toLocaleString('sv-SE', { timeZone: 'Asia/Seoul' });
    const [y, m, d] = kstString.split(' ')[0].split('-').map(n => parseInt(n, 10));
    const date = new Date(Date.UTC(y, m - 1, d));

    // ISO week number 계산 (월요일 시작)
    // 목요일 기준 주차 식별: 날짜를 해당 주의 목요일로 이동
    const dayNum = (date.getUTCDay() + 6) % 7; // 월=0
    date.setUTCDate(date.getUTCDate() + 3 - dayNum);
    const thursday = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

    const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
    const diff = thursday - firstThursday;
    const week = 1 + Math.round(diff / (7 * 24 * 3600 * 1000));

    const year = thursday.getUTCFullYear();
    const weekStr = String(week).padStart(2, '0');
    return `${year}-W${weekStr}`;
};

/**
 * ISO 문자열을 한국시간으로 해석하여 Date 객체 반환
 * @param {string} isoString - ISO 문자열
 * @returns {Date} 한국시간으로 해석된 Date 객체
 */
export const parseKoreanTime = (isoString) => {
    if (!isoString) return new Date();
    // ISO 문자열을 한국시간대로 해석
    return new Date(isoString + '+09:00');
};

/**
 * 한국시간을 로컬 문자열로 포맷팅
 * @param {string|Date} dateInput - ISO 문자열 또는 Date 객체
 * @param {object} options - toLocaleString 옵션
 * @returns {string} 포맷팅된 한국시간 문자열
 */
export const formatKoreanTime = (dateInput, options = {}) => {
    if (!dateInput) return '';

    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    const defaultOptions = {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        ...options
    };

    return date.toLocaleString('ko-KR', defaultOptions);
};

/**
 * 한국시간을 날짜만 표시하는 문자열로 포맷팅
 * @param {string|Date} dateInput - ISO 문자열 또는 Date 객체
 * @returns {string} 포맷팅된 한국 날짜 문자열
 */
export const formatKoreanDate = (dateInput) => {
    return formatKoreanTime(dateInput, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

/**
 * 한국시간을 시간만 표시하는 문자열로 포맷팅
 * @param {string|Date} dateInput - ISO 문자열 또는 Date 객체
 * @returns {string} 포맷팅된 한국 시간 문자열
 */
export const formatKoreanTimeOnly = (dateInput) => {
    return formatKoreanTime(dateInput, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

/**
 * 한국시간을 간단한 형태로 포맷팅 (예: "1월 15일 14:30")
 * @param {string|Date} dateInput - ISO 문자열 또는 Date 객체
 * @returns {string} 간단한 한국시간 문자열
 */
export const formatKoreanTimeShort = (dateInput) => {
    if (!dateInput) return '';

    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    return date.toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

/**
 * 한국시간을 사용자 친화적인 형태로 포맷팅 (예: "2025년 9월 27일 오후 9시 32분")
 * @param {string|Date} dateInput - ISO 문자열 또는 Date 객체
 * @returns {string} 사용자 친화적인 한국시간 문자열
 */
export const formatKoreanTimeUserFriendly = (dateInput) => {
    if (!dateInput) return '';

    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    return date.toLocaleString('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};
